import { ArrowDownIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosInstance } from "axios";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { FreeTool, freeToolStatuses } from "./available-tools";
import { getFileUploadUrl } from "./upload-file-action";
import { generateAssetId } from "@/app/(signed-in)/create/_shared/upload-assets/asset-ids";
import { getPlanLimits } from "@/helpers/cost-to-frame-plan";
import { trackEvent } from "@/shared/analytics-wrapper";
import { userVideoCdnBaseUrl } from "@/shared/constants";
import { errorMessage } from "@/shared/error-response";

type Version = "v1" | "v2" | "v3";

type Tool = {
  name: FreeTool;
  /**
   * @default v1
   */
  version?: Version;
};

class ToolsApiInstance<Data> {
  axios: AxiosInstance;
  tool: FreeTool;
  version: Version;
  assetPrefix: string;

  constructor(tool: Tool, assetPrefix?: string) {
    this.tool = tool.name;
    this.assetPrefix = assetPrefix || tool.name;
    this.version = tool.version || "v1";

    this.axios = axios.create({
      baseURL: `/api/free-tools/${tool.version}/${tool.name}`,
      headers: {
        "x-timezone-offset": new Date().getTimezoneOffset(),
      },
      validateStatus: function (status) {
        return status < 500;
      },
    });
  }

  startTask = (data: Data) => {
    return this.axios({ method: "post", data });
  };

  getStatus = (taskId: string) => {
    return this.axios<{
      status: string;
      limit: number;
      remaining: number;
      position?: number;
      error?: string;
      /**
       * @deprecated - to be replaced with `files` to avoid having all generated content be public
       */
      path?: string[];
      /**
       * @deprecated - to be replaced with `files` to avoid having all generated content be public
       */
      urls?: string[];
      files?: { path: string; url: string }[];
    }>({
      method: "get",
      url: `${taskId}/status`,
    });
  };

  cancelTask = (taskId: string) => {
    return this.axios({ method: "delete", url: `${taskId}` });
  };

  uploadFile = async (taskId: string, file: File, fileNamePrefix: string) => {
    const upload = await getFileUploadUrl(
      `generated/${this.assetPrefix}/${taskId}`,
      `${fileNamePrefix}-${file.name}`,
      file.type
    );
    if (!upload) {
      throw new Error("File upload failed, please try again");
    }

    await axios.put(upload.url, file, {
      headers: { "Content-Type": "application/octet-stream" },
    });

    return upload.path;
  };
}

export function useFreeTool<Data extends Record<string, unknown>>(
  tool: Tool,
  options: { assetPrefix?: string; refetchInternalMs?: number } = {}
) {
  const [api] = useState(new ToolsApiInstance<Data>(tool, options.assetPrefix));
  const [taskId, setTaskId] = useState<string>(uuidV4());
  const [status, setStatus] = useState<string>();
  const [position, setPosition] = useState<number>();

  /**
   * @deprecated - use files instead
   */
  const [outputPath, setOutputPath] = useState<string[]>();
  const [files, setFiles] = useState<{ path: string; url: string }[]>();
  const [error, setError] = useState<string>();
  const [remaining, setRemaining] = useState<number>();
  const [limit, setLimit] = useState<number>();
  const [secondsToReset, setSecondsToReset] = useState<number>();

  useEffect(() => {
    const fetchLimitInfo = async () => {
      const limitInfo = await api.getStatus("00000000-0000-0000-0000-000000000000");
      setLimit(limitInfo.data.limit);
      setRemaining(limitInfo.data.remaining);
    };
    fetchLimitInfo();
  }, []);

  useEffect(() => {
    const cancelJob = () => {
      api.cancelTask(taskId);
    };

    if (status === freeToolStatuses.pending) {
      window.addEventListener("beforeunload", cancelJob);
    }
    return () => {
      window.removeEventListener("beforeunload", cancelJob);
    };
  }, [status]);

  const incompleteStatus = [freeToolStatuses.pending, freeToolStatuses.starting];
  useQuery({
    queryKey: [tool, taskId],
    queryFn: async () => {
      if (!taskId) return;

      const { data } = await api.getStatus(taskId);

      setStatus(data.status);
      setPosition(data.position);

      if (data.urls) {
        setOutputPath(data.urls);
      } else if (data.path) {
        const fullUrls = data.path.map((path) => `${userVideoCdnBaseUrl}/${path}`);
        setOutputPath(fullUrls);
      }

      if (data.files) {
        setFiles(data.files);
      }

      if (data.error) {
        setError(errorMessage[data.error] || errorMessage["unknown_error"]);
      }

      if (typeof data.limit === "number") {
        setLimit(data.limit);
      }
      if (typeof data.remaining === "number") {
        setRemaining(data.remaining);
      }

      return data;
    },
    enabled: incompleteStatus.includes(status ?? "") && !!taskId,
    refetchInterval: incompleteStatus.includes(status ?? "")
      ? options.refetchInternalMs || 2000
      : Infinity,
  });

  function uploadFile(file: File, fileNamePrefix: string) {
    setStatus(freeToolStatuses.queued);
    const id = taskId || uuidV4();
    setTaskId(id);
    return api.uploadFile(id, file, fileNamePrefix);
  }

  async function startTask(data: Data, regenerateId = false) {
    let id: string;
    setStatus(freeToolStatuses.queued);

    if (regenerateId) {
      id = uuidV4();
      setTaskId(id);
    } else {
      id = taskId;
    }

    try {
      const r = await api.startTask({ ...data, task_id: id });

      if (r.status === 200) {
        setStatus(freeToolStatuses.pending);
      } else if (r.status === 429) {
        setStatus(freeToolStatuses.outOfCredit);
        if (typeof r.data.retryAfter === "number") {
          setSecondsToReset(r.data.retryAfter);
        }
        if (typeof r.data.limit === "number") {
          setLimit(r.data.limit);
        }
      }
    } catch (e: unknown) {
      setStatus(freeToolStatuses.error);
    }
  }

  function reset() {
    setTaskId(uuidV4());
    setStatus(undefined);
    setPosition(undefined);
    setOutputPath(undefined);
  }

  function cancelTask() {
    api.cancelTask(taskId);
    reset();
  }

  return {
    status,
    limit,
    remaining,
    secondsToReset,
    error,
    position,
    outputPath,
    files,
    startTask,
    cancelTask,
    uploadFile,
    reset,
  };
}

type ModalContent = {
  heading: string;
  cta: {
    text: string;
    link: string;
  };
  benefitChange?: {
    title: string;
    benefits: { title: string; before: string; after: string }[];
  };
};

const freePlan = getPlanLimits("free");
const creatorPlan = getPlanLimits("creator");

function convertSeconds(seconds: number): string {
  if (seconds >= 3600) {
    const hours = Math.ceil(seconds / 3600);
    return `${hours} hour${hours > 1 ? "s" : ""}`;
  }

  // Less than an hour
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes > 1 ? "s" : ""}`;
}

function getModalContent(
  tool: FreeTool,
  signedIn: boolean,
  limit: number | undefined,
  secondsToReset: number | undefined
): ModalContent {
  const reachedLimitText = limit
    ? `You've reached today's limit of ${limit} free`
    : `You've reached today's limit`;

  const tryAgainLaterText = secondsToReset
    ? `Try again in ${convertSeconds(secondsToReset)}`
    : `Try again tomorrow`;

  if (tool === "ai-clothes-changer") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} virtual try-ons. ${tryAgainLaterText}, or use our full clothes changer with more features.`,
        cta: {
          text: "Use Full AI Clothes Changer",
          link: "/create/ai-clothes-changer",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} virtual try-ons. ${tryAgainLaterText}, or sign up to access more virtual try-ons and unlock our full product.`,
        cta: {
          text: "Sign Up for More Virtual Try-Ons",
          link: "/create/ai-clothes-changer",
        },
      };
    }
  }
  if (tool === "ai-face-editor") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} face edits. ${tryAgainLaterText}, or use our full face editor with more features.`,
        cta: {
          text: "Use Full AI Face Editor",
          link: "/create/ai-face-editor",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} face edits. ${tryAgainLaterText}, or sign up to access more face edits and unlock our full product.`,
        cta: {
          text: "Sign Up for More Face Edits",
          link: "/create/ai-face-editor",
        },
      };
    }
  }
  if (tool === "ai-headshot-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} headshots. ${tryAgainLaterText}, or use our full headshot generator with more features.`,
        cta: {
          text: "Use Full AI Headshot Generator",
          link: "/create/ai-headshot-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} headshots. ${tryAgainLaterText}, or sign up to access more headshots and unlock our full product.`,
        cta: {
          text: "Sign Up for More Headshots",
          link: "/create/ai-headshot-generator",
        },
      };
    }
  }

  if (tool === "ai-image-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} image generations. ${tryAgainLaterText}, or use our full image generator with more features.`,
        cta: {
          text: "Use Full AI Image Generator",
          link: "/create/ai-image-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} image generations. ${tryAgainLaterText}, or sign up to access more generations and unlock our full product.`,
        cta: {
          text: "Sign Up for More Generations",
          link: "/create/ai-image-generator",
        },
      };
    }
  }

  if (tool === "ai-image-upscaler") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} upscales. ${tryAgainLaterText}, or use our full upscale product with more features.`,
        cta: {
          text: "Use Full AI Image Upscaler",
          link: "/create/ai-image-upscaler",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} upscales. ${tryAgainLaterText}, or sign up to access more upscales and unlock our full product.`,
        cta: {
          text: "Sign Up for More Upscales",
          link: "/create/ai-image-upscaler",
        },
      };
    }
  }
  if (tool === "ai-gif-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} GIFs. ${tryAgainLaterText}, or use our full GIF generator product with more features.`,
        cta: {
          text: "Use Full AI GIF Generator",
          link: "/create/ai-gif-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} GIFs. ${tryAgainLaterText}, or sign up to access more GIFs and unlock our full product.`,
        cta: {
          text: "Sign Up for More GIFs",
          link: "/create/ai-gif-generator",
        },
      };
    }
  }
  if (tool === "ai-qr-code-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} QR codes. ${tryAgainLaterText}, or use our full QR code generator product with more features.`,
        cta: {
          text: "Use Full AI QR Code Generator",
          link: "/create/ai-qr-code-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} QR codes. ${tryAgainLaterText}, or sign up to access more QR codes and unlock our full product.`,
        cta: {
          text: "Sign Up for More QR Codes",
          link: "/create/ai-qr-code-generator",
        },
      };
    }
  }

  if (tool === "ai-talking-photo") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} Talking Photos. ${tryAgainLaterText}, or use our full talking photo product with more features.`,
        cta: {
          text: "Use Full AI Talking Photos",
          link: "/create/ai-talking-photo",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} Talking Photos. ${tryAgainLaterText}, or sign up to access more talking photos and unlock our full product.`,
        cta: {
          text: "Sign Up for More Talking Photos",
          link: "/create/ai-talking-photo",
        },
      };
    }
  }

  if (tool === "auto-subtitle-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} subtitle generations. ${tryAgainLaterText}, or use our full subtitle generator product with more features.`,
        cta: {
          text: "Use Full Auto Subtitle Generator",
          link: "/create/auto-subtitle-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} subtitle generations. ${tryAgainLaterText}, or sign up to access more subtitle generations and unlock our full product.`,
        cta: {
          text: "Sign Up for More Subtitle Generations",
          link: "/create/auto-subtitle-generator",
        },
      };
    }
  }

  if (tool === "image-background-remover") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} image background removals. ${tryAgainLaterText}, or use our full image background remover with more features.`,
        cta: {
          text: "Use Full Image Background Remover",
          link: "/create/image-background-remover",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} image background removals. ${tryAgainLaterText}, or sign up to access more image background removals and unlock our full product.`,
        cta: {
          text: "Sign Up for More Background Removals",
          link: "/create/image-background-remover",
        },
      };
    }
  }

  if (tool === "face-swap/image") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} face swap images. ${tryAgainLaterText}, or use our full face swap image product with more features.`,
        cta: {
          text: "Use Full Face Swap Image Tool",
          link: "/create/face-swap-image",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} face swap images. ${tryAgainLaterText}, or use our full face swap image tool with more features.`,
        cta: {
          text: "Sign Up for More Face Swap Images",
          link: "/create/face-swap-image",
        },
      };
    }
  }

  if (tool === "face-swap/video") {
    const benefitChange = {
      title: "With Creator Plan",
      benefits: [
        {
          title: "Video Resolution",
          before: `${freePlan.maxLength.ANIMATION}x${freePlan.maxLength.ANIMATION}`,
          after: `${creatorPlan.maxLength.ANIMATION}x${creatorPlan.maxLength.ANIMATION}`,
        },
        {
          title: `Max Video Length`,
          before: `10 seconds`,
          after: `4 minutes`,
        },
        {
          title: `Watermark`,
          before: `Yes`,
          after: `No`,
        },
      ],
    };
    if (signedIn) {
      return {
        heading: `${reachedLimitText} face swap videos. ${tryAgainLaterText}, or use our full face swap video product with more features.`,
        cta: {
          text: "Use Full Face Swap Video Tool",
          link: "/create/face-swap-video",
        },
        benefitChange,
      };
    } else {
      return {
        heading: `${reachedLimitText} face swap videos. ${tryAgainLaterText}, or use our full face swap video tool with more features.`,
        cta: {
          text: "Sign Up for More Face Swap Videos",
          link: "/create/face-swap-video",
        },
        benefitChange,
      };
    }
  }

  if (tool === "lip-sync") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} lip syncs. ${tryAgainLaterText}, or use our full lip sync product with more features.`,
        cta: {
          text: "Use Full Lip Sync Tool",
          link: "/create/lip-sync",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} lip syncs. ${tryAgainLaterText}, or use our full lip sync tool with more features.`,
        cta: {
          text: "Sign Up for More lip syncs",
          link: "/create/lip-sync",
        },
      };
    }
  }

  if (tool === "ai-meme-generator") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} memes. ${tryAgainLaterText}, or use our full meme generator with more features.`,
        cta: {
          text: "Use Full AI Meme Generator",
          link: "/create/ai-meme-generator",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} memes. ${tryAgainLaterText}, or sign up to access more memes and unlock our full product.`,
        cta: {
          text: "Sign Up for More Memes",
          link: "/create/ai-meme-generator",
        },
      };
    }
  }
  if (tool === "photo-colorizer") {
    if (signedIn) {
      return {
        heading: `${reachedLimitText} photo colorizations. ${tryAgainLaterText}, or use our full photo colorizer with more features.`,
        cta: {
          text: "Use Full Photo Colorizer",
          link: "/create/photo-colorizer",
        },
      };
    } else {
      return {
        heading: `${reachedLimitText} photo colorizations. ${tryAgainLaterText}, or sign up to access more photo colorizations and unlock our full product.`,
        cta: {
          text: "Sign Up for More Photo Colorizations",
          link: "/create/photo-colorizer",
        },
      };
    }
  }
  // default content
  if (signedIn) {
    return {
      heading: `You've reached today's limit of ${limit} usage. ${tryAgainLaterText}, or use our full tool with more features.`,
      cta: {
        text: "Use Full Product",
        link: "/create",
      },
    };
  } else {
    return {
      heading: `You've reached today's limit of ${limit} usage. ${tryAgainLaterText}, or sign up to access more generations and unlock our full product.`,
      cta: {
        text: "Sign Up for More Generations",
        link: "/create",
      },
    };
  }
}

export function OutOfCreditModal({
  open,
  handleClose,
  tool,
  limit,
  secondsToReset,
}: {
  open: boolean;
  handleClose: () => void;
  tool: FreeTool;
  limit: number | undefined;
  secondsToReset: number | undefined;
}) {
  const { data: session } = useSession();
  const router = useRouter();

  useEffect(() => {
    trackEvent({ eventName: "freeToolsDailyLimitReachedModal", action: "view" });
  }, []);

  const closeModal = () => {
    trackEvent({ eventName: "freeToolsDailyLimitReachedModal", action: "dismiss" });
    handleClose();
  };

  const content = getModalContent(tool, !!session, limit, secondsToReset);

  const { heading, benefitChange, cta } = content;

  return (
    <dialog className="modal modal-bottom sm:modal-middle" open={open}>
      <div className="modal-box w-full max-w-xl! border border-slate-800 bg-slate-900 shadow-xl">
        <button
          className="btn btn-circle btn-ghost btn-sm absolute top-2 right-2"
          onClick={closeModal}
        >
          ✕
        </button>
        <h3 className="text-center text-xl font-bold">Daily Limit Reached</h3>
        <p className="pt-4 text-center">{heading}</p>
        {benefitChange && (
          <>
            <p className="mb-1 pt-4 text-center text-xl">{benefitChange.title}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {benefitChange.benefits.map((benefit) => {
                return <BenefitChange key={benefit.title} {...benefit} />;
              })}
            </div>
          </>
        )}

        <div className="modal-action flex justify-center">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              trackEvent({ eventName: "freeToolsDailyLimitReachedModal", action: "click" });
              router.push(`${cta.link}?int=daily-limit-reached-modal`);
            }}
          >
            {cta.text}
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50">
        <button onClick={closeModal}>close</button>
      </div>
    </dialog>
  );
}

function BenefitChange({ title, before, after }: { title: string; before: string; after: string }) {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-700 p-2">
      <strong className="text-center capitalize">{title}</strong>

      <div className="grid grid-cols-5 gap-1 sm:flex sm:flex-col sm:items-center sm:justify-center">
        <div className="col-span-2 text-right">{before}</div>
        <div className="m-auto -rotate-90 sm:rotate-0">
          <ArrowDownIcon className="text-success mx-auto h-6 w-6" />
        </div>
        <div className="col-span-2 flex items-center justify-start">{after}</div>
      </div>
    </div>
  );
}
